import * as React from 'react';
import Backdrop from '@mui/material/Backdrop';
import X from 'assets/x.inline.svg';
import Modal from '@mui/material/Modal';
import useEmployeeModalImages from 'about/hooks/useEmployeeModalImages';
import { MedicalExpert } from 'lib/types';
import ImageWrapper from 'core/components/ImageWrapper';

import { BioType } from '@bondvet/types/website';
import { QuestionnaireTeamMemberBio } from '@bondvet/types/clinicTeam';
import useTeamDatabaseQuestions from 'core/hooks/useTeamDatabaseQuestions';
import * as styles from './TeamMembers.module.scss';
import { MappedEmployee } from './useSortedTeamMembers';

type QuestionWithAnswer = {
    question: string;
    answer: string;
    orderId: number;
};

export interface TeamMemberModalProps {
    employee?: MappedEmployee;
    medicalExpert?: MedicalExpert;
    customImageURL?: string;
    modalOpen: boolean;
    onClose: () => void;
}

function TeamMemberModal({
    employee: employeeData,
    medicalExpert,
    customImageURL,
    modalOpen,
    onClose,
}: TeamMemberModalProps) {
    const images = useEmployeeModalImages(employeeData);
    const questions = useTeamDatabaseQuestions();

    const { employee, isMedicalExpert } = React.useMemo(() => {
        if (employeeData) {
            return {
                employee: employeeData,
                isMedicalExpert: false,
            };
        }
        if (medicalExpert) {
            return {
                employee: medicalExpert.teamMember,
                isMedicalExpert: true,
            };
        }
        return {
            employee: null,
            isMedicalExpert: null,
        };
    }, [employeeData, medicalExpert]);

    const vetSchool = React.useMemo(() => {
        if (employee?.bio?.type === BioType.doctorQuestionnaire) {
            const vetSchoolQuestion = questions.find(
                ({ type }) => type === 'vetSchool',
            );

            if (vetSchoolQuestion?.display) {
                return employee.bio.questions?.[vetSchoolQuestion._id] ?? null;
            }
        }

        return null;
    }, [questions, employee?.bio]);

    const content = React.useMemo(() => {
        if (employee) {
            if (
                employee.bio?.type === BioType.generalQuestionnaire ||
                employee.bio?.type === BioType.doctorQuestionnaire
            ) {
                const questionsWithAnswers = Object.entries(
                    (employee.bio as QuestionnaireTeamMemberBio).questions,
                )
                    .reduce((acc: QuestionWithAnswer[], [id, answer]) => {
                        const question = questions.find(
                            ({ _id, display, type }) =>
                                _id === id && display && type !== 'vetSchool',
                        );

                        if (question) {
                            acc.push({
                                question: question.clientQuestion,
                                answer,
                                orderId: question.orderId,
                            });
                        }
                        return acc;
                    }, [])
                    .sort((a, b) => a.orderId - b.orderId);

                return (
                    <div className={styles.modalText}>
                        {questionsWithAnswers.map(
                            ({ question, answer, orderId }) => (
                                <div key={orderId} className={styles.paragraph}>
                                    <h4>{question}</h4>
                                    <div>{answer}</div>
                                </div>
                            ),
                        )}
                    </div>
                );
            }
            if (employee.bio?.type === BioType.freeform) {
                return (
                    <div className={styles.modalText}>{employee.bio.value}</div>
                );
            }
            return <div className={styles.modalText}>Coming Soon!</div>;
        }
        return null;
    }, [questions, employee]);

    return (
        <Modal
            open={modalOpen}
            onClose={onClose}
            className={styles.modalWrapper}
            slots={{
                backdrop: ({ open }) => (
                    <Backdrop
                        open={open}
                        classes={{ root: styles.backdrop }}
                        onClick={onClose}
                    />
                ),
            }}
        >
            <div className={styles.modal}>
                <button
                    className={styles.close}
                    onClick={onClose}
                    type="button"
                >
                    <span className="sr-only">Close</span>
                    <X />
                </button>
                <div className={styles.container}>
                    {employee && (
                        <div
                            className={
                                isMedicalExpert
                                    ? styles.medicalExpert
                                    : styles.employee
                            }
                        >
                            <div className={styles.modalHeader}>
                                {customImageURL ? (
                                    <img
                                        className={
                                            isMedicalExpert
                                                ? styles.medicalExpertImage
                                                : styles.image
                                        }
                                        src={customImageURL}
                                        alt={employee.name}
                                    />
                                ) : (
                                    <ImageWrapper
                                        className={styles.image}
                                        image={
                                            isMedicalExpert
                                                ? employee.image?.file
                                                : images
                                        }
                                        alt={employee.name}
                                        loading="eager"
                                    />
                                )}
                                <div>
                                    <h2 className={styles.name}>
                                        {employee.includePrefix ? 'Dr. ' : ''}
                                        {employee.name}
                                    </h2>
                                    <p className={styles.role}>
                                        {employee.role}
                                    </p>
                                    {vetSchool && (
                                        <p className={styles.vetSchool}>
                                            {vetSchool}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {content}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default React.memo(TeamMemberModal);
